/**
 * Task Pool Modal Component
 * Modal for viewing global tasks not on the current board and adding them
 * Reads store data directly via hooks.
 */

import {
  memo,
  useState,
  useMemo,
  useCallback,
  useEffect,
  useRef,
  type ReactNode,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useModalDrag } from '../../../hooks/useModalDrag';
import { X, Search } from 'lucide-react';
import { useSelector } from '@xstate/store/react';
import type { GlobalTask } from '../../../types';
import { fuzzyFilter } from '../../utils/fuzzySearch';
import { TaskPoolTaskItem } from './TaskPoolTaskItem';
import { TaskPoolModalContext } from './TaskPoolModalContext';
import { globalTasksStore } from '../../../stores/globalTasksStore';
import { useActiveBoardContext } from '../../stores/ActiveBoardContext';
import { useColumns, useColumnNames, useTaskCategories } from '../../../stores/hooks';
export { useOpenTaskPoolModal } from './TaskPoolModalContext';

// ============================================
// Provider
// ============================================

interface TaskPoolModalProviderProps {
  children: ReactNode;
  onAddToBoard?: (taskId: string, column: string) => void;
}

export function TaskPoolModalProvider({
  children,
  onAddToBoard,
}: TaskPoolModalProviderProps) {
  const [state, setState] = useState<{ isOpen: boolean; column: string | null }>({
    isOpen: false,
    column: null,
  });

  const openModal = useCallback((column?: string) => {
    setState({ isOpen: true, column: column ?? null });
  }, []);

  const closeModal = useCallback(() => {
    setState({ isOpen: false, column: null });
  }, []);

  const contextValue = useMemo(
    () => ({ openModal }),
    [openModal]
  );

  return (
    <TaskPoolModalContext.Provider value={contextValue}>
      {children}
      {state.isOpen && (
        <TaskPoolModalInner
          column={state.column}
          onClose={closeModal}
          onAddToBoard={onAddToBoard}
        />
      )}
    </TaskPoolModalContext.Provider>
  );
}

// ============================================
// Modal Inner Component
// ============================================

interface TaskPoolModalInnerProps {
  column: string | null;
  onClose: () => void;
  onAddToBoard?: (taskId: string, column: string) => void;
}

const TaskPoolModalInner = memo(function TaskPoolModalInner({
  column,
  onClose,
  onAddToBoard,
}: TaskPoolModalInnerProps) {
  const { t } = useTranslation();
  const { modalRef, position, isDragging, handleMouseDown, handleDoubleClick } = useModalDrag();

  // Read data from stores
  const columnDefinitions = useColumns();
  const columnNames = useColumnNames();
  const categories = useTaskCategories();
  const { boardTaskViewStore } = useActiveBoardContext();
  const boardViews = useSelector(boardTaskViewStore, (s) => s.context.views);
  const boardTaskIds = useMemo(() => new Set(Object.keys(boardViews)), [boardViews]);
  const globalTasksCtx = useSelector(globalTasksStore, (s) => s.context);
  const globalTasks = useMemo(
    () => globalTasksCtx.taskIds.map(id => globalTasksCtx.tasksById[id]).filter(Boolean) as GlobalTask[],
    [globalTasksCtx]
  );

  const columnDef = useMemo(
    () => column !== null ? columnDefinitions.find(c => c.name === column) : undefined,
    [columnDefinitions, column]
  );

  const [searchQuery, setSearchQuery] = useState('');
  const [filterCategoryIds, setFilterCategoryIds] = useState<string[]>(
    () => columnDef?.defaultCategoryIds ?? []
  );

  // Fallback column for adding tasks when opened without a column scope
  const effectiveColumn = column ?? columnNames[0] ?? '';
  const inputRef = useRef<HTMLInputElement>(null);

  // Tasks not on this board
  const poolTasks = useMemo(
    () => globalTasks.filter(t => !boardTaskIds.has(t.id)),
    [globalTasks, boardTaskIds]
  );

  // Filter by categories
  const categoryFilteredTasks = useMemo(() => {
    if (filterCategoryIds.length === 0) return poolTasks;
    return poolTasks.filter(t =>
      (t.categoryIds ?? []).some(id => filterCategoryIds.includes(id))
    );
  }, [poolTasks, filterCategoryIds]);

  // Fuzzy search
  const filteredTasks = useMemo(
    () => searchQuery.trim()
      ? fuzzyFilter(categoryFilteredTasks, searchQuery, (t) => t.title)
      : categoryFilteredTasks,
    [categoryFilteredTasks, searchQuery]
  );

  // Focus input on mount
  useEffect(() => {
    setTimeout(() => inputRef.current?.focus(), 50);
  }, []);

  // ESC to close
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  const handleAddToBoard = useCallback(
    (taskId: string) => {
      onAddToBoard?.(taskId, effectiveColumn);
    },
    [onAddToBoard, effectiveColumn]
  );

  const toggleCategory = useCallback((categoryId: string) => {
    setFilterCategoryIds(prev =>
      prev.includes(categoryId)
        ? prev.filter(id => id !== categoryId)
        : [...prev, categoryId]
    );
  }, []);

  return (
    <>
      {/* Overlay */}
      <div
        onClick={onClose}
        style={{
          position: 'fixed',
          inset: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          zIndex: 60,
        }}
      />

      {/* Modal */}
      <div
        style={{
          position: 'fixed',
          inset: 0,
          zIndex: 70,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: 16,
          pointerEvents: 'none',
        }}
      >
        <div
          ref={modalRef}
          style={position ? {
            position: 'fixed',
            left: position.x,
            top: position.y,
            backgroundColor: '#fff',
            borderRadius: 8,
            boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.15)',
            width: '100%',
            maxWidth: 480,
            maxHeight: 'calc(100vh - 64px)',
            display: 'flex',
            flexDirection: 'column',
            pointerEvents: 'auto',
          } : {
            backgroundColor: '#fff',
            borderRadius: 8,
            boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.15)',
            width: '100%',
            maxWidth: 480,
            maxHeight: 'calc(100vh - 64px)',
            display: 'flex',
            flexDirection: 'column',
            pointerEvents: 'auto',
          }}
        >
          {/* Header */}
          <div
            onMouseDown={handleMouseDown}
            onDoubleClick={handleDoubleClick}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '6px 12px',
              borderBottom: '1px solid #e5e7eb',
              cursor: isDragging ? 'grabbing' : 'grab',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
              <span style={{ fontSize: 12 }}>📋</span>
              <h3 style={{ margin: 0, fontSize: 11, fontWeight: 500, color: '#1f2937' }}>
                {column !== null ? `Task-Pool: ${column}` : 'Task-Pool'}
              </h3>
            </div>
            <button
              onClick={onClose}
              onMouseDown={e => e.stopPropagation()}
              style={{
                padding: 2,
                color: '#6b7280',
                backgroundColor: 'transparent',
                border: 'none',
                borderRadius: 3,
                cursor: 'pointer',
                display: 'flex',
              }}
            >
              <X size={14} />
            </button>
          </div>

          {/* Category Filter Chips */}
          {categories.length > 0 && (
            <div style={{ padding: '4px 12px', borderBottom: '1px solid #e5e7eb', display: 'flex', flexWrap: 'wrap', gap: 3 }}>
              {categories.map((cat) => {
                const isActive = filterCategoryIds.includes(cat.id);
                return (
                  <button
                    key={cat.id}
                    onClick={() => toggleCategory(cat.id)}
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 2,
                      fontSize: 10,
                      padding: '1px 6px',
                      borderRadius: 9999,
                      border: `1px solid ${isActive ? cat.color : '#d1d5db'}`,
                      backgroundColor: isActive ? cat.color + '20' : '#fff',
                      color: isActive ? cat.color : '#6b7280',
                      cursor: 'pointer',
                      transition: 'all 0.15s',
                      fontWeight: isActive ? 600 : 400,
                    }}
                  >
                    <span
                      style={{
                        width: 6,
                        height: 6,
                        borderRadius: '50%',
                        backgroundColor: isActive ? cat.color : '#d1d5db',
                      }}
                    />
                    {cat.name}
                  </button>
                );
              })}
              {filterCategoryIds.length > 0 && (
                <button
                  onClick={() => setFilterCategoryIds([])}
                  style={{
                    fontSize: 9,
                    padding: '1px 4px',
                    borderRadius: 9999,
                    border: 'none',
                    backgroundColor: 'transparent',
                    color: '#9ca3af',
                    cursor: 'pointer',
                    textDecoration: 'underline',
                  }}
                >
                  {t('boardv3.taskPool.all')}
                </button>
              )}
            </div>
          )}

          {/* Search */}
          <div style={{ padding: '4px 12px', borderBottom: '1px solid #e5e7eb' }}>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 4,
                padding: '3px 8px',
                backgroundColor: '#f9fafb',
                border: '1px solid #e5e7eb',
                borderRadius: 4,
              }}
            >
              <Search size={12} style={{ color: '#9ca3af', flexShrink: 0 }} />
              <input
                ref={inputRef}
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder={t('boardv3.taskPool.searchTask')}
                style={{
                  flex: 1,
                  border: 'none',
                  outline: 'none',
                  backgroundColor: 'transparent',
                  fontSize: 11,
                  color: '#1f2937',
                }}
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery('')}
                  style={{
                    padding: 1,
                    color: '#9ca3af',
                    backgroundColor: 'transparent',
                    border: 'none',
                    cursor: 'pointer',
                    display: 'flex',
                  }}
                >
                  <X size={10} />
                </button>
              )}
            </div>
          </div>

          {/* Task List */}
          <div style={{ flex: 1, overflow: 'auto', minHeight: 120, maxHeight: 320 }}>
            {filteredTasks.length === 0 ? (
              <div style={{ padding: 16, textAlign: 'center', color: '#6b7280' }}>
                {searchQuery ? (
                  <>
                    <p style={{ margin: 0, fontSize: 10 }}>
                      {t('boardv3.taskPool.noTasksFound', { query: searchQuery })}
                    </p>
                    <button
                      onClick={() => setSearchQuery('')}
                      style={{
                        marginTop: 4,
                        fontSize: 10,
                        color: '#3b82f6',
                        backgroundColor: 'transparent',
                        border: 'none',
                        cursor: 'pointer',
                      }}
                    >
                      {t('boardv3.taskPool.resetSearch')}
                    </button>
                  </>
                ) : (
                  <p style={{ margin: 0, fontSize: 10 }}>
                    {poolTasks.length === 0
                      ? t('boardv3.taskPool.allTasksOnBoard')
                      : t('boardv3.taskPool.noMatchingTasks')}
                  </p>
                )}
              </div>
            ) : (
              filteredTasks.map((task) => (
                <TaskPoolTaskItem
                  key={task.id}
                  task={task}
                  categories={categories}
                  searchQuery={searchQuery}
                  onAddToBoard={handleAddToBoard}
                />
              ))
            )}
          </div>

          {/* Footer */}
          <div
            style={{
              padding: '4px 12px',
              borderTop: '1px solid #e5e7eb',
              backgroundColor: '#f9fafb',
              borderRadius: '0 0 8px 8px',
              fontSize: 10,
              color: '#6b7280',
              display: 'flex',
              justifyContent: 'space-between',
            }}
          >
            <span>
              {filteredTasks.length} von {poolTasks.length} Tasks
            </span>
            <span style={{ fontSize: 9 }}>ESC zum Schliessen</span>
          </div>
        </div>
      </div>
    </>
  );
});

export default TaskPoolModalProvider;
